(() => {
  // TRM Installation Quote Tracking V5 — token-persistent submit deduplication
  // plus one confirmed-success Meta Lead event through the existing pixel.
  const ROOT_SELECTOR = '[data-trm-installation-quote]';
  const DEFAULT_STORAGE_KEY = 'trmSelectedVehicleV1';
  const FORM_NAME = 'trm_installation_quote_v1';
  const FORM_DESTINATION = 'shopify_forms';
  const LEAD_TYPE = 'installation_quote';
  const SOURCE_PAGE = 'installation_quote';
  const SUCCESS_TEXT_MARKERS = [
    'your request has been received',
    'thank you. the form has been submitted',
    'request submitted'
  ];
  const PENDING_KEY = 'trm-installation-quote-pending-v1';
  const CONSUMED_KEY = 'trm-installation-quote-consumed-v1';
  const SUBMIT_SENT_KEY = 'trm-installation-quote-submit-sent-v1';
  const META_LEAD_SENT_KEY = 'trm-installation-quote-meta-lead-sent-v1';
  const PENDING_MAX_AGE_MS = 30 * 60 * 1000;
  const SUBMIT_RETRY_UNLOCK_MS = 12 * 1000;
  const META_LEAD_RETRY_MS = 250;
  const META_LEAD_MAX_ATTEMPTS = 40;

  const initializedRoots = new WeakSet();
  const boundFields = new WeakSet();
  const boundForms = new WeakSet();
  const observedRoots = new WeakSet();
  const stateByRoot = new WeakMap();

  const normalize = (value = '') =>
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const cleanDimension = (value = '', maxLength = 100) =>
    value
      .toString()
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);

  const titleCase = (value = '') =>
    value
      .toString()
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase())
      .replace(/\b4runner\b/i, '4Runner')
      .replace(/\bF (\d{3})\b/g, 'F-$1')
      .replace(/\bRam\b/g, 'RAM')
      .trim();

  const safeJsonParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const readVehicle = (storageKey) => {
    try {
      const parsed = safeJsonParse(
        window.localStorage.getItem(storageKey || DEFAULT_STORAGE_KEY)
      );

      if (!parsed?.key || !parsed?.label) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const getAttribution = () => {
    const url = new URL(window.location.href);
    const keys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'gclid',
      'fbclid',
      'source'
    ];
    const attribution = {};

    keys.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value) attribution[key] = value;
    });

    return attribution;
  };

  const createToken = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const getState = (root) => {
    if (!stateByRoot.has(root)) {
      stateByRoot.set(root, {
        formStarted: false,
        submitLocked: false,
        submitUnlockTimer: null,
        submitEventFired: false,
        successEventsFired: false,
        sanitizedSnapshot: null,
        pendingToken: '',
        successRendered: false
      });
    }

    return stateByRoot.get(root);
  };

  const getSearchRoots = (startNode) => {
    const roots = [];
    const visited = new Set();

    const visit = (root) => {
      if (!root || visited.has(root)) return;
      visited.add(root);
      roots.push(root);

      const elements = root.querySelectorAll?.('*') || [];
      elements.forEach((element) => {
        if (element.shadowRoot) visit(element.shadowRoot);
      });
    };

    visit(startNode);
    return roots;
  };

  const associatedLabelText = (field, searchRoot) => {
    const values = [
      field.getAttribute('aria-label'),
      field.getAttribute('placeholder'),
      field.getAttribute('name'),
      field.getAttribute('data-label')
    ];

    const id = field.getAttribute('id');
    if (id && searchRoot.querySelector) {
      try {
        values.push(
          searchRoot.querySelector(`label[for="${CSS.escape(id)}"]`)?.textContent
        );
      } catch (error) {
        // Ignore malformed generated IDs.
      }
    }

    values.push(field.closest('label')?.textContent);
    values.push(field.parentElement?.querySelector(':scope > label')?.textContent);
    values.push(field.parentElement?.previousElementSibling?.textContent);

    return normalize(values.filter(Boolean).join(' '));
  };

  const findField = (host, labels) => {
    const targets = labels.map(normalize);

    for (const searchRoot of getSearchRoots(host)) {
      const fields = searchRoot.querySelectorAll?.('input, select, textarea') || [];

      for (const field of fields) {
        const descriptor = associatedLabelText(field, searchRoot);

        if (targets.some((target) => descriptor.includes(target))) {
          return { field, searchRoot };
        }
      }
    }

    return null;
  };

  const isTechnicalField = (field, searchRoot) => {
    if (!field) return true;
    if (field.dataset.trmTechnicalField === 'source-page') return true;
    if (field.type === 'hidden') return true;

    const descriptor = associatedLabelText(field, searchRoot);
    return descriptor.includes('source page');
  };

  const setFieldValue = (field, value) => {
    if (!field || value == null || value === '') return false;

    if (field.tagName === 'SELECT') {
      const normalizedValue = normalize(value);
      const option = Array.from(field.options).find(
        (item) =>
          normalize(item.value) === normalizedValue ||
          normalize(item.textContent) === normalizedValue
      );

      if (!option) return false;
      field.value = option.value;
    } else if (!field.value) {
      field.value = value;
    } else {
      return false;
    }

    field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    return true;
  };

  const hideElement = (element) => {
    if (!element) return;

    element.hidden = true;
    element.setAttribute('aria-hidden', 'true');
    element.style.setProperty('display', 'none', 'important');
  };

  const hideTechnicalField = ({ field, searchRoot }) => {
    if (!field) return;

    field.dataset.trmTechnicalField = 'source-page';
    field.setAttribute('tabindex', '-1');
    field.setAttribute('aria-hidden', 'true');
    field.setAttribute('autocomplete', 'off');

    if (field instanceof HTMLInputElement) {
      try {
        field.type = 'hidden';
        field.setAttribute('type', 'hidden');
      } catch (error) {
        // Fall through to CSS-based hiding.
      }
    }

    const labels = [];
    const id = field.getAttribute('id');

    if (id && searchRoot?.querySelector) {
      try {
        const label = searchRoot.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (label) labels.push(label);
      } catch (error) {
        // Ignore malformed generated IDs.
      }
    }

    const closestLabel = field.closest('label');
    if (closestLabel) labels.push(closestLabel);

    const directLabel = field.parentElement?.querySelector(':scope > label');
    if (directLabel) labels.push(directLabel);

    labels.forEach(hideElement);

    let candidate = field.parentElement;
    let fieldContainer = null;

    for (let depth = 0; candidate && depth < 10; depth += 1) {
      const controls = Array.from(
        candidate.querySelectorAll('input, select, textarea')
      );

      if (controls.length === 1 && controls[0] === field) {
        fieldContainer = candidate;
        break;
      }

      candidate = candidate.parentElement;
    }

    if (fieldContainer) {
      hideElement(fieldContainer);
      return;
    }

    field.style.setProperty('display', 'none', 'important');
    field.style.setProperty('position', 'absolute', 'important');
    field.style.setProperty('inline-size', '1px', 'important');
    field.style.setProperty('block-size', '1px', 'important');
    field.style.setProperty('overflow', 'hidden', 'important');
    field.style.setProperty('clip-path', 'inset(50%)', 'important');
    field.style.setProperty('white-space', 'nowrap', 'important');
    field.style.setProperty('pointer-events', 'none', 'important');
  };

  const compactSourceValue = (vehicle) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('submitted');

    const parts = [`URL: ${url.href}`];
    const attribution = getAttribution();

    if (document.referrer) {
      try {
        const referrer = new URL(document.referrer);
        parts.push(`Ref: ${referrer.host}${referrer.pathname}`);
      } catch (error) {
        parts.push(`Ref: ${document.referrer}`);
      }
    }

    if (attribution.source) parts.push(`Source: ${attribution.source}`);
    if (attribution.utm_source) parts.push(`UTM source: ${attribution.utm_source}`);
    if (attribution.utm_medium) parts.push(`UTM medium: ${attribution.utm_medium}`);
    if (attribution.utm_campaign) parts.push(`Campaign: ${attribution.utm_campaign}`);
    if (attribution.gclid) parts.push('Google click: yes');
    if (attribution.fbclid) parts.push('Meta click: yes');
    if (vehicle?.label) parts.push(`Garage: ${vehicle.label}`);
    if (vehicle?.key) parts.push(`Vehicle key: ${vehicle.key}`);

    return parts.join(' | ').slice(0, 500);
  };

  const prefillForm = (root) => {
    const host = root.querySelector('[data-trm-installation-form-host]');
    if (!host) return false;

    const url = new URL(window.location.href);
    const vehicle = readVehicle(root.dataset.garageKey);
    let changed = false;

    const queryValues = {
      year: url.searchParams.get('year') || '',
      make: url.searchParams.get('make') || '',
      model: url.searchParams.get('model') || '',
      service: url.searchParams.get('service') || '',
      part:
        url.searchParams.get('product') ||
        url.searchParams.get('product_url') ||
        url.searchParams.get('part') ||
        ''
    };

    const makeValue =
      queryValues.make || (vehicle?.makeKey ? titleCase(vehicle.makeKey) : '');
    const modelValue =
      queryValues.model || (vehicle?.modelKey ? titleCase(vehicle.modelKey) : '');

    const mappings = [
      { labels: ['year'], value: queryValues.year },
      { labels: ['make'], value: makeValue },
      { labels: ['model'], value: modelValue },
      { labels: ['what can we help you with'], value: queryValues.service },
      { labels: ['part number or product link'], value: queryValues.part }
    ];

    mappings.forEach(({ labels, value }) => {
      const match = findField(host, labels);
      if (match && setFieldValue(match.field, value)) changed = true;
    });

    const sourceField = findField(host, ['source page']);
    if (sourceField) {
      if (!sourceField.field.value) {
        sourceField.field.value = compactSourceValue(vehicle);
        sourceField.field.dispatchEvent(
          new Event('input', { bubbles: true, composed: true })
        );
        sourceField.field.dispatchEvent(
          new Event('change', { bubbles: true, composed: true })
        );
      }

      hideTechnicalField(sourceField);
      changed = true;
    }

    return changed;
  };

  const getFieldValue = (host, labels) =>
    cleanDimension(findField(host, labels)?.field?.value || '');

  const getSanitizedSnapshot = (root) => {
    const host = root.querySelector('[data-trm-installation-form-host]');
    if (!host) {
      return {
        service_category: '',
        vehicle_year: '',
        vehicle_make: '',
        vehicle_model: ''
      };
    }

    return {
      service_category: getFieldValue(host, ['what can we help you with']),
      vehicle_year: getFieldValue(host, ['year'], 10),
      vehicle_make: getFieldValue(host, ['make']),
      vehicle_model: getFieldValue(host, ['model'])
    };
  };

  const analyticsParameters = (eventName, snapshot = {}) => {
    const base = {
      form_name: FORM_NAME
    };

    if (eventName === 'form_start') {
      return {
        ...base,
        form_destination: FORM_DESTINATION
      };
    }

    if (eventName === 'installation_quote_submit') {
      return {
        ...base,
        form_destination: FORM_DESTINATION,
        service_category: snapshot.service_category || '',
        vehicle_year: snapshot.vehicle_year || '',
        vehicle_make: snapshot.vehicle_make || '',
        vehicle_model: snapshot.vehicle_model || '',
        source_page: SOURCE_PAGE
      };
    }

    if (
      eventName === 'generate_lead' ||
      eventName === 'installation_quote_success'
    ) {
      return {
        ...base,
        lead_type: LEAD_TYPE,
        service_category: snapshot.service_category || '',
        vehicle_year: snapshot.vehicle_year || '',
        vehicle_make: snapshot.vehicle_make || '',
        vehicle_model: snapshot.vehicle_model || '',
        source_page: SOURCE_PAGE
      };
    }

    return base;
  };

  const publishShopifyDebugEvent = (eventName, parameters, attempts = 0) => {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(`trm:${eventName}`, parameters);
      return;
    }

    if (attempts < 20) {
      window.setTimeout(
        () => publishShopifyDebugEvent(eventName, parameters, attempts + 1),
        250
      );
    }
  };

  const sendGoogleEvent = (eventName, parameters) => {
    /*
      No tag, pixel, or Measurement ID is loaded here. This uses the existing
      Google tag when Shopify's Google & YouTube integration exposes gtag.
      The dataLayer fallback supports existing tag-manager implementations.
    */
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, parameters);
      return 'gtag';
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: eventName,
        ...parameters
      });
      return 'dataLayer';
    }

    return 'unavailable';
  };

  const publishAnalyticsEvent = (eventName, snapshot = {}) => {
    const parameters = analyticsParameters(eventName, snapshot);
    const transport = sendGoogleEvent(eventName, parameters);

    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          ...parameters,
          transport
        }
      })
    );

    publishShopifyDebugEvent(eventName, {
      ...parameters,
      transport
    });
  };

  const readPending = () => {
    try {
      const pending = safeJsonParse(window.sessionStorage.getItem(PENDING_KEY));
      if (!pending?.token || !pending?.createdAt || !pending?.snapshot) return null;

      if (Date.now() - Number(pending.createdAt) > PENDING_MAX_AGE_MS) {
        window.sessionStorage.removeItem(PENDING_KEY);
        return null;
      }

      return pending;
    } catch (error) {
      return null;
    }
  };

  const writePending = (token, snapshot) => {
    try {
      window.sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          token,
          createdAt: Date.now(),
          snapshot
        })
      );
    } catch (error) {
      // Session storage is optional; in-memory state still works.
    }
  };

  const consumePending = (token) => {
    try {
      const consumed =
        safeJsonParse(window.sessionStorage.getItem(CONSUMED_KEY)) || [];
      const updated = Array.from(new Set([...consumed.slice(-19), token]));

      window.sessionStorage.setItem(CONSUMED_KEY, JSON.stringify(updated));
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch (error) {
      // Ignore storage restrictions.
    }
  };

  const isConsumed = (token) => {
    try {
      const consumed =
        safeJsonParse(window.sessionStorage.getItem(CONSUMED_KEY)) || [];

      return consumed.includes(token);
    } catch (error) {
      return false;
    }
  };

  const wasSubmitEventSent = (token) => {
    if (!token) return false;

    try {
      const sent =
        safeJsonParse(window.sessionStorage.getItem(SUBMIT_SENT_KEY)) || [];

      return sent.includes(token);
    } catch (error) {
      return false;
    }
  };

  const markSubmitEventSent = (token) => {
    if (!token) return;

    try {
      const sent =
        safeJsonParse(window.sessionStorage.getItem(SUBMIT_SENT_KEY)) || [];
      const updated = Array.from(new Set([...sent.slice(-19), token]));

      window.sessionStorage.setItem(SUBMIT_SENT_KEY, JSON.stringify(updated));
    } catch (error) {
      // Session storage is optional; in-memory locking still provides protection.
    }
  };

  const wasMetaLeadSent = (token) => {
    if (!token) return false;

    try {
      const sent =
        safeJsonParse(window.sessionStorage.getItem(META_LEAD_SENT_KEY)) || [];

      return sent.includes(token);
    } catch (error) {
      return false;
    }
  };

  const markMetaLeadSent = (token) => {
    if (!token) return;

    try {
      const sent =
        safeJsonParse(window.sessionStorage.getItem(META_LEAD_SENT_KEY)) || [];
      const updated = Array.from(new Set([...sent.slice(-19), token]));

      window.sessionStorage.setItem(
        META_LEAD_SENT_KEY,
        JSON.stringify(updated)
      );
    } catch (error) {
      // Session storage is optional; the confirmed-success state still limits calls.
    }
  };

  const sendMetaLead = (token, attempts = 0) => {
    if (!token || wasMetaLeadSent(token)) return false;

    /*
      Do not initialize another pixel here. Shopify's existing Facebook &
      Instagram partner integration owns the base pixel / dataset connection.
      The empty event-parameter object guarantees that no form fields or other
      personally identifiable information are sent to Meta.
    */
    if (typeof window.fbq === 'function') {
      try {
        window.fbq('track', 'Lead', {}, { eventID: token });
        markMetaLeadSent(token);

        document.dispatchEvent(
          new CustomEvent('trm:meta_lead_sent', {
            detail: {
              event_name: 'Lead',
              event_id: token,
              source_page: SOURCE_PAGE
            }
          })
        );

        return true;
      } catch (error) {
        // Retry briefly in case the partner-managed pixel is still initializing.
      }
    }

    if (attempts < META_LEAD_MAX_ATTEMPTS) {
      window.setTimeout(
        () => sendMetaLead(token, attempts + 1),
        META_LEAD_RETRY_MS
      );
    }

    return false;
  };

  const fireFormStart = (root) => {
    const state = getState(root);
    if (state.formStarted) return;

    state.formStarted = true;
    publishAnalyticsEvent('form_start');
  };

  const fireValidatedSubmit = (root, form = null) => {
    const state = getState(root);

    if (state.submitLocked) return false;

    if (
      form &&
      typeof form.checkValidity === 'function' &&
      !form.checkValidity()
    ) {
      return false;
    }

    const snapshot = getSanitizedSnapshot(root);
    const token = createToken();

    state.submitLocked = true;
    state.submitEventFired = true;
    state.sanitizedSnapshot = snapshot;
    state.pendingToken = token;

    writePending(token, snapshot);
    markSubmitEventSent(token);
    publishAnalyticsEvent('installation_quote_submit', snapshot);

    if (state.submitUnlockTimer) {
      window.clearTimeout(state.submitUnlockTimer);
    }

    state.submitUnlockTimer = window.setTimeout(() => {
      if (!state.successEventsFired) state.submitLocked = false;
    }, SUBMIT_RETRY_UNLOCK_MS);

    return true;
  };

  const renderCustomSuccess = (root) => {
    const state = getState(root);
    if (state.successRendered) return;

    const formCard = root.querySelector('[data-trm-quote-form-card]');
    const success = root.querySelector('[data-trm-quote-success]');

    if (formCard) formCard.hidden = true;

    if (success) {
      success.hidden = false;
      window.setTimeout(() => success.focus({ preventScroll: true }), 0);
    }

    /*
      Keep the visitor on the same canonical page URL. Shopify Forms already
      confirmed the submission in-page, so no synthetic success query parameter
      is needed and no hidden-page/404 redirect can occur.
    */
    state.successRendered = true;
  };

  const confirmSuccessfulSubmission = (root, pendingOverride = null) => {
    const state = getState(root);
    if (state.successEventsFired) return false;

    const pending = pendingOverride || readPending();
    const token = pending?.token || state.pendingToken;
    const snapshot = pending?.snapshot || state.sanitizedSnapshot;

    /*
      A success event needs a pending, validated submit token. Therefore a plain
      page view or a manually typed ?submitted=1 URL can never create a lead.
    */
    if (!token || !snapshot || isConsumed(token)) return false;

    /*
      Shopify Forms can replace parts of its app block after a successful submit.
      That can create a fresh DOM root and therefore a fresh in-memory state.
      The token-level session marker survives that replacement and guarantees
      installation_quote_submit is emitted at most once for this submit attempt.
    */
    if (!state.submitEventFired && !wasSubmitEventSent(token)) {
      markSubmitEventSent(token);
      publishAnalyticsEvent('installation_quote_submit', snapshot);
    }

    state.submitEventFired = true;

    publishAnalyticsEvent('generate_lead', snapshot);
    publishAnalyticsEvent('installation_quote_success', snapshot);
    sendMetaLead(token);

    state.successEventsFired = true;
    state.submitLocked = true;
    state.sanitizedSnapshot = snapshot;
    state.pendingToken = token;

    consumePending(token);
    renderCustomSuccess(root);

    return true;
  };

  const rootContainsSuccessMessage = (host) => {
    for (const searchRoot of getSearchRoots(host)) {
      const text = normalize(searchRoot.textContent || '');

      if (
        SUCCESS_TEXT_MARKERS.some((marker) =>
          text.includes(normalize(marker))
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const bindFieldTracking = (root) => {
    const host = root.querySelector('[data-trm-installation-form-host]');
    if (!host) return;

    getSearchRoots(host).forEach((searchRoot) => {
      const fields =
        searchRoot.querySelectorAll?.('input, select, textarea') || [];

      fields.forEach((field) => {
        if (boundFields.has(field) || isTechnicalField(field, searchRoot)) {
          return;
        }

        boundFields.add(field);

        ['focusin', 'input', 'change'].forEach((eventName) => {
          field.addEventListener(eventName, () => fireFormStart(root), {
            capture: true,
            once: false
          });
        });
      });
    });
  };

  const bindFormTracking = (root) => {
    const host = root.querySelector('[data-trm-installation-form-host]');
    if (!host) return;

    getSearchRoots(host).forEach((searchRoot) => {
      const forms = searchRoot.querySelectorAll?.('form') || [];

      forms.forEach((form) => {
        if (boundForms.has(form)) return;

        boundForms.add(form);

        form.addEventListener(
          'submit',
          () => {
            fireFormStart(root);
            fireValidatedSubmit(root, form);
          },
          true
        );

        /*
          formdata is a standards-based backup for implementations that build
          FormData during a valid submission. Duplicate protection prevents a
          second event when submit already fired.
        */
        form.addEventListener(
          'formdata',
          () => {
            fireFormStart(root);
            fireValidatedSubmit(root, form);
          },
          true
        );
      });
    });
  };

  const observeSearchRoots = (root) => {
    const host = root.querySelector('[data-trm-installation-form-host]');

    if (!host || !('MutationObserver' in window)) return;

    const refresh = () => {
      prefillForm(root);
      bindFieldTracking(root);
      bindFormTracking(root);

      if (rootContainsSuccessMessage(host)) {
        confirmSuccessfulSubmission(root);
      }

      getSearchRoots(host).forEach((searchRoot) => {
        if (observedRoots.has(searchRoot)) return;

        observedRoots.add(searchRoot);

        const observer = new MutationObserver(refresh);

        observer.observe(searchRoot, {
          childList: true,
          subtree: true,
          characterData: true
        });
      });
    };

    refresh();
  };

  const renderGarageContext = (root) => {
    const garage = root.querySelector('[data-trm-quote-garage]');
    const label = root.querySelector('[data-trm-quote-garage-label]');
    const vehicle = readVehicle(root.dataset.garageKey);

    if (!garage || !label) return;

    if (!vehicle?.label) {
      garage.hidden = true;
      label.textContent = '';
      return;
    }

    label.textContent = vehicle.label;
    garage.hidden = false;
  };

  const handleChangeVehicle = () => {
    const globalChangeButton = document.querySelector(
      '[data-trm-garage-change]'
    );
    const garage = document.querySelector('[data-trm-garage]');

    if (globalChangeButton) globalChangeButton.click();

    if (garage) {
      garage.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const recoverConfirmedRedirect = (root) => {
    const url = new URL(window.location.href);
    const successParam = root.dataset.successParam || 'submitted';
    const successValue = root.dataset.successValue || '1';

    if (url.searchParams.get(successParam) !== successValue) return false;

    /*
      Redirect recovery is accepted only when this browser session contains the
      validated submit token written immediately before Shopify submission.
    */
    return confirmSuccessfulSubmission(root, readPending());
  };

  const publishSimpleSafeEvent = (eventName, parameters = {}) => {
    const safe = Object.fromEntries(
      Object.entries(parameters).map(([key, value]) => [
        key,
        cleanDimension(value)
      ])
    );

    const transport = sendGoogleEvent(eventName, safe);

    publishShopifyDebugEvent(eventName, {
      ...safe,
      transport
    });
  };

  const initRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;

    initializedRoots.add(root);
    renderGarageContext(root);

    root.addEventListener('click', (event) => {
      const callLink = event.target.closest('[data-trm-click-to-call]');

      if (callLink) {
        publishSimpleSafeEvent('click_to_call', {
          link_text: callLink.textContent.trim(),
          source_page: SOURCE_PAGE
        });
      }

      const changeVehicle = event.target.closest(
        '[data-trm-quote-change-vehicle]'
      );

      if (changeVehicle) handleChangeVehicle();
    });

    window.addEventListener('trm:vehicle-changed', () => {
      renderGarageContext(root);
      prefillForm(root);
    });

    window.addEventListener('trm:vehicle-cleared', () => {
      renderGarageContext(root);
    });

    const url = new URL(window.location.href);

    if (url.searchParams.get('source') === 'fitment-help') {
      publishSimpleSafeEvent('fitment_help_request', {
        source_page: SOURCE_PAGE,
        request_source: 'fitment_help'
      });
    }

    if (recoverConfirmedRedirect(root)) return;

    let attempts = 0;
    const maxAttempts = 100;

    const tryInitializeForm = () => {
      attempts += 1;

      prefillForm(root);
      bindFieldTracking(root);
      bindFormTracking(root);
      observeSearchRoots(root);

      const host = root.querySelector('[data-trm-installation-form-host]');
      const hasFields =
        host &&
        getSearchRoots(host).some((searchRoot) =>
          searchRoot.querySelector?.('input, select, textarea')
        );

      if (!hasFields && attempts < maxAttempts) {
        window.setTimeout(tryInitializeForm, 200);
      }
    };

    tryInitializeForm();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();